import { SelectPositionButton } from 'components/minute/sidebar/shared/SelectPositionButton';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { NumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/NumberValue';
import { LocationSearchInput } from 'elements/minute/sidebar/sidebarComponents/complex/LocationSearchInput';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { useAccess } from 'hooks/access/useAccess';
import { useContext } from 'react';
import { ICoordinates } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    coordinates: ICoordinates[];
    onCoordinatesChange?: (coordinates: ICoordinates[]) => void;
}

export const MarkerCoordinatesSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const { isExperimental } = useAccess();

    function removeCoordinate(index: number) {
        const newCoordinates = props.coordinates
            .slice(0, index)
            .concat(props.coordinates.slice(index + 1));
        props.onCoordinatesChange?.(newCoordinates);
    }

    function updateCoordinate(index: number, coordinate: ICoordinates) {
        const newCoordinates = props.coordinates
            .slice(0, index)
            .concat(coordinate)
            .concat(props.coordinates.slice(index + 1));
        props.onCoordinatesChange?.(newCoordinates);
    }

    return (
        <S_MarkerCoordinatesSection className={props.className}>
            <SidebarSection
                title={Localization.COORDINATES}
                icon={{ iconName: 'vector-polygon' }}
                topBorder
            >
                {props.coordinates.map((_, i) => (
                    <SidebarSubsection
                        // eslint-disable-next-line react/no-array-index-key
                        key={'|' + i}
                        expandable={true}
                        initialExpanded={false}
                        title={Localization.formatString(Localization.POINT_X, i + 1)?.toString()}
                        icon={{ iconName: 'location-dot' }}
                        disableBottomBorder={i === props.coordinates.length - 1}
                    >
                        <SidebarContent>
                            <SidebarSectionRow>
                                <LocationSearchInput
                                    onLocationSelected={({ lat, lng }) => {
                                        updateCoordinate(i, { lat, lng });
                                    }}
                                />
                            </SidebarSectionRow>
                            {isExperimental && (
                                <SidebarSectionRow>
                                    <S_NumberValue
                                        label={Localization.LATITUDE}
                                        value={props.coordinates[i].lat}
                                        onChange={(value) => {
                                            updateCoordinate(i, {
                                                lat: value,
                                                lng: props.coordinates[i].lng
                                            });
                                        }}
                                    />
                                    <S_NumberValue
                                        label={Localization.LONGITUDE}
                                        value={props.coordinates[i].lng}
                                        onChange={(value) => {
                                            updateCoordinate(i, {
                                                lat: props.coordinates[i].lat,
                                                lng: value
                                            });
                                        }}
                                    />
                                </SidebarSectionRow>
                            )}
                            <SidebarSectionRow alignment="end">
                                <S_ButtonsRow>
                                    <SelectPositionButton
                                        mapRef={ctxEdit.mapRef.current}
                                        onCoordinateSelected={(coordinate) => {
                                            updateCoordinate(i, coordinate);
                                        }}
                                    />
                                    <ActionButton
                                        icon={{ iconName: 'trash' }}
                                        onClick={() => {
                                            removeCoordinate(i);
                                        }}
                                    />
                                </S_ButtonsRow>
                            </SidebarSectionRow>
                        </SidebarContent>
                    </SidebarSubsection>
                ))}
            </SidebarSection>
        </S_MarkerCoordinatesSection>
    );
};

const S_MarkerCoordinatesSection = styled.div``;

const S_ButtonsRow = styled.div`
    display: flex;
    flex-direction: row;
    column-gap: 15px;
`;

const S_NumberValue = styled(NumberValue)`
    width: 100%;
    &:first-child {
        margin: 0 8px 0 0;
    }
    &:last-child {
        margin: 0 0 0 8px;
    }
`;
